import { rem } from '../../utils/utils';
import { DropdownProps } from './DropdownProps';

type DropdownStateProps = Pick<DropdownProps<string | null>, 'disabled' | 'readOnly' | 'isWarning' | 'isError'> & {
  placeholder: boolean;
  isOpen: boolean;
  isMultiSelect: boolean;
  hasAvatar: boolean;
};

const getCursor = ({ readOnly, disabled }: DropdownStateProps) => {
  if (disabled) {
    return 'not-allowed';
  }

  if (readOnly) {
    return 'default';
  }

  return 'pointer';
};

const getBorderColor = ({ readOnly, disabled, isError }: DropdownStateProps) => {
  if (isError) {
    return 'border/error';
  }

  if (readOnly || disabled) {
    return 'border/disabled';
  }

  return 'border/strong';
};

const getIconColor = ({ disabled, isOpen, readOnly }: DropdownStateProps) => {
  if (isOpen) {
    return 'icon/primary';
  }

  if (disabled || readOnly) {
    return 'icon/on-disabled';
  }

  return 'icon/secondary';
};

const getHelperTextColor = ({ disabled, isWarning }: DropdownStateProps) => {
  if (isWarning) {
    return 'text/body';
  }

  if (disabled) {
    return 'text/disabled';
  }

  return 'input/text/helper';
};

const getButtonContentColor = ({ disabled, placeholder }: DropdownStateProps) => {
  if (disabled) {
    return 'text/disabled';
  }

  if (placeholder) {
    return 'text/secondary';
  }

  return 'input/text/inputValue';
};

const DropdownTheme = {
  baseStyle: (props: DropdownStateProps) => {
    const { disabled, isMultiSelect, readOnly, hasAvatar, isError } = props;
    return {
      field: {
        _hover:
          disabled || readOnly || isError
            ? undefined
            : {
                borderColor: 'border/hover',
                color: 'text/primary',
              },
        alignItems: 'center',
        backgroundColor: disabled || readOnly ? 'background/disabled' : 'background/primary',
        border: '0.0625rem solid',
        borderColor: getBorderColor(props),
        borderRadius: '4',
        cursor: getCursor(props),
        display: 'flex',
        position: 'relative',
        textAlign: 'start',
        width: '100%',
      },
      buttonContent: {
        color: getButtonContentColor(props),
      },
      buttonContentHover: {
        color: disabled || readOnly ? undefined : 'text/primary',
      },
      group: {
        alignItems: 'center',
        display: 'flex',
        flexDir: 'row',
        gap: '12',
        paddingX: '16',
        paddingY: '8',
      },
      helperText: {
        color: getHelperTextColor(props),
      },
      icon: {
        color: getIconColor(props),
        _groupHover: {
          color: disabled || readOnly ? 'icon/on-disabled' : 'icon/primary',
        },
      },
      item: {
        '&.bitkit-select__option-active': {
          '&.bitkit-select__option-hover': {
            backgroundColor: 'purple.93',
          },
          backgroundColor: 'purple.95',
        },
        '&.bitkit-select__option-hover': {
          backgroundColor: 'neutral.95',
        },
        _disabled: {
          color: 'neutral.70',
          cursor: 'not-allowed',
        },
        color: 'purple.10',
        cursor: 'pointer',
        paddingLeft: hasAvatar ? '12' : '16',
        paddingRight: '24',
        paddingY: isMultiSelect ? '12' : '8',
        textAlign: 'start',
        userSelect: 'none',
        w: '100%',
        wordBreak: 'break-word',
      },
      list: {
        backgroundColor: 'neutral.100',
        border: '1px solid',
        borderColor: 'neutral.93',
        borderRadius: '4',
        boxShadow: 'large',
        display: 'flex',
        flexDir: 'column',
        flexShrink: 1,
        maxH: 'max(min(var(--dropdown-floating-max, 16rem), var(--floating-available-height)), var(--dropdown-floating-min, 10rem))',
        minH: 0,
        zIndex: '1',
      },
      options: {
        flexShrink: 1,
        overflowY: 'auto',
        paddingY: '8',
        position: 'relative',
      },
      search: {
        marginTop: '12',
        mx: 16,
        width: 'auto',
      },
    };
  },
  sizes: {
    lg: ({ hasAvatar, isMultiSelect }: DropdownStateProps) => {
      return {
        field: {
          fontSize: '3',
          gap: rem('8'),
          height: isMultiSelect ? 'auto' : '48',
          minH: isMultiSelect ? '48' : undefined,
          paddingLeft: hasAvatar ? rem('12') : rem('16'),
          paddingRight: rem('16'),
          paddingTop: isMultiSelect ? rem('7') : '0',
          paddingBottom: isMultiSelect ? rem('7') : '0',
        },
      };
    },
    md: ({ isMultiSelect }: DropdownStateProps) => {
      return {
        field: {
          fontSize: '2',
          gap: rem('8'),
          height: isMultiSelect ? 'auto' : '40',
          minH: isMultiSelect ? '40' : undefined,
          paddingLeft: rem('12'),
          paddingRight: rem('12'),
          paddingTop: isMultiSelect ? rem('8') : '0',
          paddingBottom: isMultiSelect ? rem('8') : '0',
        },
        item: {
          fontSize: '2',
          paddingLeft: rem('16'),
          paddingRight: rem('16'),
          paddingTop: isMultiSelect ? rem('8') : undefined,
          paddingBottom: isMultiSelect ? rem('8') : undefined,
        },
      };
    },
  },
};

export default DropdownTheme;
